import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { TrendingDown, TrendingUp, Globe, Loader2, AlertTriangle } from 'lucide-react';

/**
 * PriceTooltip — wraps a child element. When the user hovers it (≥ 350ms),
 * the component fires a one-shot call to /api/owner/price-lookup and shows
 * a small floating card that follows the mouse pointer. Owners + admin only.
 *
 * Usage:
 *   <PriceTooltip name="Aashirvaad Atta 5kg" category="kirana">
 *     <span>Aashirvaad Atta 5kg</span>
 *   </PriceTooltip>
 */
export default function PriceTooltip({ name, category, unit, children, testIdSuffix = '' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const hoverTimer = useRef(null);
  const fetchedRef = useRef(false);

  const fetchPrice = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/owner/price-lookup', { product_name: name, category, unit });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.status === 403
        ? 'Restricted to shop owners & admin'
        : 'Could not fetch price right now');
    } finally {
      setLoading(false);
    }
  }, [name, category, unit]);

  const onEnter = (e) => {
    setPos({ x: e.clientX, y: e.clientY });
    hoverTimer.current = setTimeout(() => {
      setOpen(true);
      fetchPrice();
    }, 350);
  };
  const onMove = (e) => setPos({ x: e.clientX, y: e.clientY });
  const onLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setOpen(false);
  };

  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); }, []);

  // Position the floating card so it never overflows the viewport
  const cardStyle = (() => {
    const W = 270, H = 160, pad = 14;
    let left = pos.x + pad;
    let top = pos.y + pad;
    if (typeof window !== 'undefined') {
      if (left + W > window.innerWidth) left = pos.x - W - pad;
      if (top + H > window.innerHeight) top = pos.y - H - pad;
    }
    return { left, top };
  })();

  return (
    <span
      data-testid={`price-tooltip-anchor${testIdSuffix ? `-${testIdSuffix}` : ''}`}
      onMouseEnter={onEnter}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative cursor-help underline decoration-dotted decoration-[#0D47A1]/40 underline-offset-2 hover:decoration-[#E67E22]"
    >
      {children}
      {open && (
        <div
          data-testid={`price-tooltip-card${testIdSuffix ? `-${testIdSuffix}` : ''}`}
          className="fixed z-[60] w-[270px] rounded-xl border border-[#0D47A1]/20 bg-white shadow-2xl p-3 pointer-events-auto animate-in fade-in zoom-in-95"
          style={cardStyle}
          onMouseEnter={() => { if (hoverTimer.current) clearTimeout(hoverTimer.current); setOpen(true); }}
          onMouseLeave={onLeave}
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[#0D47A1]">
            <Globe className="w-3 h-3"/>Live Indian market price
          </div>
          <div className="mt-1 font-heading font-bold text-sm text-ink line-clamp-2">{name}</div>

          {loading && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs text-ink/60">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#E67E22]"/>Checking live prices…
            </div>
          )}

          {error && !loading && (
            <div className="mt-2 inline-flex items-start gap-1.5 text-xs text-[#9A4500]">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5"/>{error}
            </div>
          )}

          {data && !loading && !error && (
            <>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-[#FFF3E0] border border-[#FFCC80] p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#9A4500] inline-flex items-center gap-1"><TrendingUp className="w-3 h-3"/>Avg</div>
                  <div className="font-heading font-black text-base text-ink mt-0.5 tabular-nums">
                    {data.avg_price_inr != null ? `₹${data.avg_price_inr}` : '—'}
                  </div>
                </div>
                <div className="rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] p-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#1B5E20] inline-flex items-center gap-1"><TrendingDown className="w-3 h-3"/>Lowest</div>
                  <div className="font-heading font-black text-base text-[#1B5E20] mt-0.5 tabular-nums">
                    {data.lowest_price_inr != null ? `₹${data.lowest_price_inr}` : '—'}
                  </div>
                </div>
              </div>

              {(data.source_name || data.source_url) && (
                <div className="mt-2 text-[11px] text-ink/70 leading-snug">
                  Source:{' '}
                  {data.source_url ? (
                    <a href={data.source_url} target="_blank" rel="noreferrer" className="text-[#0D47A1] hover:underline font-semibold">
                      {data.source_name || 'open link'}
                    </a>
                  ) : (
                    <span className="font-semibold">{data.source_name}</span>
                  )}
                </div>
              )}

              {data.notes && (
                <div className="mt-1 text-[11px] text-ink/60 leading-snug">{data.notes}</div>
              )}

              <div className="mt-1.5 text-[9px] uppercase tracking-wider text-ink/40">
                {data.source === 'cache' ? '6-hr cache' : data.source === 'tavily+llm' ? 'web · live' : 'estimate'}
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
